import React, { useState, useEffect } from 'react'
import {
  Container,
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  CircularProgress,
  Grid,
  Backdrop,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import CancelIcon from '@mui/icons-material/Cancel'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import DownloadIcon from '@mui/icons-material/Download'
import { getUnifiedReturns, downloadUnifiedReturns } from '../services/api'

function Dashboard() {
  const [search, setSearch] = useState('')
  const [returnType, setReturnType] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [rows, setRows] = useState([])
  const [filteredRows, setFilteredRows] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [status, setStatus] = useState('Click Refresh to load data.')
  const [loadTime, setLoadTime] = useState(null)
  const [abortController, setAbortController] = useState(null)
  const [statusOptions, setStatusOptions] = useState([])
  const [returnTypeOptions, setReturnTypeOptions] = useState([])
  const [optionsLoading, setOptionsLoading] = useState(true)

  const cancelRequest = () => {
    if (abortController) {
      abortController.abort()
      setAbortController(null)
      setLoading(false)
      setStatus('Request cancelled by user.')
      setError('Request was cancelled.')
    }
  }

  const loadData = async () => {
    // Cancel any existing request
    if (abortController) {
      abortController.abort()
    }

    // Create new AbortController for this request
    const controller = new AbortController()
    setAbortController(controller)

    const startTime = performance.now()
    setLoading(true)
    setError(null)
    setStatus('Loading...')
    setLoadTime(null)

    try {
      const filters = {}
      if (dateFrom) filters.date_from = dateFrom
      if (dateTo) filters.date_to = dateTo
      if (statusFilter) filters.status = statusFilter
      if (returnType) filters.return_type = returnType

      console.log('Fetching returns data with filters:', filters) // Debug log
      // Note: axios cancellation would need to be implemented via CancelToken or signal
      // For now, we'll rely on axios's built-in request cancellation
      const response = await getUnifiedReturns(filters)
      
      const data = response.data

      const endTime = performance.now()
      const duration = ((endTime - startTime) / 1000).toFixed(2) // Convert to seconds with 2 decimals
      setLoadTime(duration)

      const fetchedRows = Array.isArray(data.rows) ? data.rows : []
      console.log('API Response:', data) // Debug log
      console.log('Fetched rows count:', fetchedRows.length) // Debug log
      console.log('Load time:', duration, 'seconds') // Debug log
      if (fetchedRows.length > 0) {
        console.log('First row sample:', fetchedRows[0]) // Debug log
      }
      setRows(fetchedRows)
      applyFilters(fetchedRows, search, returnType, statusFilter, dateFrom, dateTo)
      setStatus(`Loaded ${fetchedRows.length} rows in ${duration}s`)
      setAbortController(null) // Clear abort controller on success
    } catch (e) {
      const endTime = performance.now()
      const duration = ((endTime - startTime) / 1000).toFixed(2)
      setLoadTime(duration)
      console.error('Error loading data:', e) // Debug log
      
      // Check if request was aborted
      if (e.name === 'AbortError' || e.code === 'ERR_CANCELED') {
        setError('Request was cancelled.')
        setStatus('Request cancelled.')
        setAbortController(null)
        return
      }
      
      let errorMessage = e?.response?.data?.error || e?.response?.data?.message || e?.message || 'Failed to load data'
      
      // Provide helpful error messages for common issues
      if (e?.response?.status === 401) {
        errorMessage = 'Authentication failed. Please log in again.'
      } else if (e?.response?.status === 403) {
        errorMessage = 'You do not have permission to access this resource.'
      } else if (e?.response?.status >= 500) {
        errorMessage = `Server error: ${errorMessage}`
      }
      
      setError(errorMessage)
      setRows([])
      setFilteredRows([])
      setStatus(`Error: ${errorMessage} (${duration}s)`)
      setAbortController(null) // Clear abort controller on error
    } finally {
      setLoading(false)
    }
  }

  const applyFilters = (
    dataRows = rows,
    searchTerm = search,
    type = returnType,
    status = statusFilter,
    fromDate = dateFrom,
    toDate = dateTo
  ) => {
    if (!dataRows || dataRows.length === 0) {
      setFilteredRows([])
      return
    }

    const q = (searchTerm || '').trim().toLowerCase()
    const filtered = dataRows.filter((r) => {
      // Return type filter
      if (type && type !== '' && formatValue(r.return_type) !== type) return false

      // Status filter
      if (status && status !== '' && formatValue(r.status) !== status) return false

      // Date filter
      if (fromDate || toDate) {
        const returnDate = r.return_date ? new Date(r.return_date) : null
        if (returnDate) {
          if (fromDate && fromDate !== '') {
            const from = new Date(fromDate)
            from.setHours(0, 0, 0, 0)
            if (returnDate < from) return false
          }
          if (toDate && toDate !== '') {
            const to = new Date(toDate)
            to.setHours(23, 59, 59, 999)
            if (returnDate > to) return false
          }
        } else if (fromDate || toDate) {
          // If date filter is set but return_date is null, exclude it
          return false
        }
      }

      // Search filter
      if (!q || q === '') return true

      const searchable = [
        r.order_name,
        r.sku,
        r.return_id,
        r.tracking_number,
        r.status,
        r.return_type,
        r.source_system,
        r.shopify_order_id,
        r.payment_id,
        r.shopify_refund_id,
      ]
        .map((x) => formatValue(x).toLowerCase())
        .join(' | ')

      return searchable.includes(q)
    })

    console.log('Filtered rows:', filtered.length, 'of', dataRows.length) // Debug log
    setFilteredRows(filtered)
    if (dataRows.length > 0) {
      setStatus(`Showing ${filtered.length} of ${dataRows.length} rows.`)
    }
  }

  // Load filter options on component mount
  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        setOptionsLoading(true)
        const response = await getUnifiedReturns({ filter_options: 'true' })
        
        const data = response.data
        if (data.statuses) {
          setStatusOptions(data.statuses)
        }
        if (data.return_types) {
          setReturnTypeOptions(data.return_types)
        }
      } catch (e) {
        console.error('Error loading filter options:', e)
        // Fallback to empty arrays if API fails
        setStatusOptions([])
        setReturnTypeOptions([])
      } finally {
        setOptionsLoading(false)
      }
    }
    
    loadFilterOptions()
  }, [])

  useEffect(() => {
    // Only apply filters if we have data loaded
    if (rows.length > 0) {
      applyFilters(rows, search, returnType, statusFilter, dateFrom, dateTo)
    }
  }, [search, returnType, statusFilter, dateFrom, dateTo])

  const formatValue = (v) => {
    if (v === null || v === undefined) return ''
    return String(v)
  }

  const formatMoney = (v) => {
    if (v === null || v === undefined || v === '') return ''
    const n = Number(v)
    if (Number.isNaN(n)) return formatValue(v)
    return n.toLocaleString(undefined, { maximumFractionDigits: 2 })
  }

  // Download filtered data as CSV from backend
  const downloadCSV = async () => {
    if (filteredRows.length === 0) {
      alert('No data to download')
      return
    }

    try {
      setLoading(true)
      setError(null)
      setStatus('Preparing download...')

      const filters = {}
      if (dateFrom) filters.date_from = dateFrom
      if (dateTo) filters.date_to = dateTo
      if (statusFilter) filters.status = statusFilter
      if (returnType) filters.return_type = returnType

      const response = await downloadUnifiedReturns(filters)
      
      // Create blob from response
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const urlObj = URL.createObjectURL(blob)
      const filename = `returns_data_${new Date().toISOString().split('T')[0]}.csv`
      
      link.setAttribute('href', urlObj)
      link.setAttribute('download', filename)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(urlObj)
      
      setStatus(`Downloaded ${filename}`)
      setError(null)
    } catch (e) {
      console.error('Error downloading CSV:', e)
      const errorMessage = e?.response?.data?.error || e?.response?.data?.message || e?.message || 'Download failed'
      setError(`Download failed: ${errorMessage}`)
      setStatus(`Download error: ${errorMessage}`)
    } finally {
      setLoading(false)
    }
  }

  const columns = [
    { id: 'return_date', label: 'Return Date', align: 'left' },
    { id: 'order_name', label: 'Order Name', align: 'left' },
    { id: 'sku', label: 'SKU', align: 'left' },
    { id: 'return_id', label: 'Return ID', align: 'left' },
    { id: 'return_type', label: 'Return Type', align: 'left' },
    { id: 'status', label: 'Status', align: 'left' },
    { id: 'tracking_number', label: 'Tracking Number', align: 'left' },
    { id: 'source_system', label: 'Source System', align: 'left' },
    { id: 'shopify_order_id', label: 'Shopify Order ID', align: 'left' },
    { id: 'payment_id', label: 'Razorpay Payment ID', align: 'left' },
    { id: 'shopify_refund_id', label: 'Razorpay Refund ID', align: 'left' },
    { id: 'payment_amount', label: 'Payment Amount', align: 'right' },
    { id: 'razorpay_refund_status', label: 'Razorpay Refund Status', align: 'left' },
    { id: 'razorpay_refund_amount', label: 'Razorpay Refund Amount', align: 'right' },
    { id: 'razorpay_refund_created_at', label: 'Refund Created At', align: 'left' },
  ]

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: 'background.default',
        backgroundImage: 'linear-gradient(180deg, #ffffff 0%, #fafafa 100%)',
      }}
    >
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box
          sx={{
            mb: 4,
            pb: 3,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{
              fontWeight: 700,
              letterSpacing: '-0.02em',
              background: 'linear-gradient(90deg,rgb(10, 10, 10) 0%, #cccccc 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            Returns Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Manage and filter return cases with advanced search capabilities
          </Typography>
        </Box>

        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '8px',
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Search (order / return / tracking / status)"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="e.g. 10523, RTO, delivered, AWB..."
                sx={{
                  '& .MuiInputBase-input': {
                    color: 'text.primary',
                  },
                }}
              />
            </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
              disabled={optionsLoading}
            >
              <MenuItem value="">All</MenuItem>
              {statusOptions.map((status) => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth>
            <InputLabel>Return Type</InputLabel>
            <Select
              value={returnType}
              label="Return Type"
              onChange={(e) => setReturnType(e.target.value)}
              disabled={optionsLoading}
            >
              <MenuItem value="">All</MenuItem>
              {returnTypeOptions.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <TextField
            fullWidth
            label="From Date"
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <TextField
            fullWidth
            label="To Date"
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>

            <Grid item xs={12} sm={6} md={1}>
              <Button
                fullWidth
                variant="contained"
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
                onClick={loadData}
                disabled={loading}
                sx={{
                  height: '56px',
                  fontWeight: 600,
                  boxShadow: 'none',
                  '&:hover': {
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                  },
                }}
              >
                Refresh
              </Button>
            </Grid>

            <Grid 
              item 
              xs={12} 
              sm={6} 
              md={1}
              sx={{
                width: '100px',
                textAlign: 'center',
                verticalAlign: 'middle',
              }}
            >
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={downloadCSV}
                disabled={filteredRows.length === 0}
                sx={{
                  width: '150px',
                  height: '50px',
                  fontWeight: 600,
                  borderColor: 'text.primary',
                  color: 'text.primary',
                  '&:hover': {
                    borderColor: 'text.primary',
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                Download
              </Button>
            </Grid>
          </Grid>
        </Paper>

        <Box
          sx={{
            mb: 3,
            p: 2,
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '4px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
            {status}
          </Typography>
          {loadTime !== null && (
            <Typography
              variant="body2"
              sx={{
                fontFamily: 'monospace',
                fontWeight: 600,
                color: loading ? 'text.secondary' : 'text.primary',
                backgroundColor: loading ? 'transparent' : 'action.hover',
                px: 2,
                py: 0.5,
                borderRadius: '4px',
              }}
            >
              ⏱️ {loadTime}s
            </Typography>
          )}
        </Box>

        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 3,
              backgroundColor: '#fff5f5',
              border: '1px solid #ff4444',
              color: '#d32f2f',
              '& .MuiAlert-icon': {
                color: '#d32f2f',
              },
            }}
          >
            {error}
          </Alert>
        )}

        <TableContainer
          component={Paper}
          sx={{
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '8px',
            maxHeight: 'calc(100vh - 400px)',
            height: '500px',
            overflowX: 'auto',
            overflowY: 'auto',
          }}
        >
          <Table stickyHeader size="small" sx={{ minWidth: '100%' }}>
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    sx={{
                      fontWeight: 700,
                      fontSize: '0.875rem',
                      letterSpacing: '0.02em',
                      backgroundColor: 'background.paper',
                      borderBottom: '2px solid',
                      borderColor: 'divider',
                      color: 'text.primary',
                    }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
          <TableBody sx={{ height: '290px' }}>
            {filteredRows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  align="center"
                  sx={{
                    py: 6,
                    height: '190px',
                    color: 'text.secondary',
                    fontSize: '1rem',
                    fontFamily: 'monospace',
                  }}
                >
                  {loading ? 'Loading...' : rows.length === 0 ? 'Click Refresh to load data.' : 'No rows match the current filters.'}
                </TableCell>
              </TableRow>
            ) : (
              filteredRows.map((row, idx) => (
                <TableRow
                  key={idx}
                  hover
                  sx={{
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                    '&:nth-of-type(even)': {
                      backgroundColor: 'rgba(0, 0, 0, 0.02)',
                    },
                  }}
                >
                  <TableCell
                    sx={{
                      fontFamily: 'monospace',
                      color: 'text.primary',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    {formatValue(row.return_date)}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: 'monospace',
                      color: 'text.primary',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    {row.shopify_order_id ? (
                      <Link
                        href={`https://admin.shopify.com/store/rarerabbit/orders/${row.shopify_order_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          color: 'primary.main',
                          textDecoration: 'none',
                          fontWeight: 500,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5,
                          '&:hover': {
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        {formatValue(row.order_name)}
                        <OpenInNewIcon sx={{ fontSize: '0.875rem' }} />
                      </Link>
                    ) : (
                      formatValue(row.order_name)
                    )}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: 'monospace',
                      color: 'text.primary',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    {formatValue(row.sku)}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: 'monospace',
                      color: 'text.primary',
                      fontWeight: 600,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    {formatValue(row.return_id)}
                  </TableCell>
                  <TableCell
                    sx={{
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    {row.return_type && (
                      <Chip
                        label={formatValue(row.return_type)}
                        size="small"
                        sx={{
                          backgroundColor: '#f5f5f5',
                          color: '#000000',
                          border: '1px solid #e0e0e0',
                          fontWeight: 500,
                        }}
                      />
                    )}
                  </TableCell>
                  <TableCell
                    sx={{
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    {row.status && (
                      <Chip
                        label={formatValue(row.status)}
                        size="small"
                        sx={{
                          backgroundColor: '#f5f5f5',
                          color: '#000000',
                          border: '1px solid #e0e0e0',
                          fontWeight: 500,
                        }}
                      />
                    )}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: 'monospace',
                      color: 'text.primary',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    {formatValue(row.tracking_number)}
                  </TableCell>
                  <TableCell
                    sx={{
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    {row.source_system && (
                      <Chip
                        label={formatValue(row.source_system)}
                        size="small"
                        sx={{
                          backgroundColor: '#f5f5f5',
                          color: '#000000',
                          border: '1px solid #e0e0e0',
                          fontWeight: 500,
                        }}
                      />
                    )}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: 'monospace',
                      color: 'text.primary',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    {formatValue(row.shopify_order_id)}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: 'monospace',
                      color: 'text.primary',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    {row.payment_id ? (
                      <Link
                        href={`https://dashboard.razorpay.com/app/payments/${row.payment_id}?init_page=Payments`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          color: 'primary.main',
                          textDecoration: 'none',
                          fontWeight: 500,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5,
                          '&:hover': {
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        {formatValue(row.payment_id)}
                        <OpenInNewIcon sx={{ fontSize: '0.875rem' }} />
                      </Link>
                    ) : (
                      formatValue(row.payment_id)
                    )}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: 'monospace',
                      color: 'text.primary',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    {row.shopify_refund_id ? (
                      <Link
                        href={`https://dashboard.razorpay.com/app/refunds/${row.shopify_refund_id}?init_page=Refunds`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          color: 'primary.main',
                          textDecoration: 'none',
                          fontWeight: 500,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5,
                          '&:hover': {
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        {formatValue(row.shopify_refund_id)}
                        <OpenInNewIcon sx={{ fontSize: '0.875rem' }} />
                      </Link>
                    ) : (
                      formatValue(row.shopify_refund_id)
                    )}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontFamily: 'monospace',
                      color: 'text.primary',
                      fontWeight: 500,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    {formatMoney(row.payment_amount)}
                  </TableCell>
                  <TableCell
                    sx={{
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    {row.razorpay_refund_status && (
                      <Chip
                        label={formatValue(row.razorpay_refund_status)}
                        size="small"
                        sx={{
                          backgroundColor: '#f5f5f5',
                          color: '#000000',
                          border: '1px solid #e0e0e0',
                          fontWeight: 500,
                        }}
                      />
                    )}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontFamily: 'monospace',
                      color: 'text.primary',
                      fontWeight: 500,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    {formatMoney(row.razorpay_refund_amount)}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: 'monospace',
                      color: 'text.primary',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    {formatValue(row.razorpay_refund_created_at)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      </Container>

      {/* Loading Overlay with Cancel Button */}
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
        }}
        open={loading}
      >
        <CircularProgress size={60} sx={{ color: '#ffffff' }} />
        <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 600 }}>
          Loading data...
        </Typography>
        <Typography variant="body2" sx={{ color: '#cccccc' }}>
          {status}
        </Typography>
        <Button
          variant="contained"
          color="error"
          startIcon={<CancelIcon />}
          onClick={cancelRequest}
          sx={{
            mt: 2,
            backgroundColor: '#ff4444',
            color: '#ffffff',
            '&:hover': {
              backgroundColor: '#cc0000',
            },
          }}
        >
          Cancel Request
        </Button>
      </Backdrop>
    </Box>
  )
}

export default Dashboard

